import React from 'react';
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Alert,
  List
} from 'antd';
import {
  InfoCircleOutlined,
  SettingOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface DocumentTypeViewerProps {
  documentType: {
    document_type: string;
    description: string;
    schema: {
      version: string;
      fields: Array<{
        name: string;
        type: string;
        required: boolean;
        description?: string;
        extraction_rules?: {
          patterns?: string[];
          keywords?: string[];
          region?: { x: number; y: number; width: number; height: number };
        };
      }>;
    };
    settings: {
      ocr_engine?: string;
      confidence_threshold?: number;
      auto_correct?: boolean;
      review_required?: boolean;
    };
    created_at: string;
    updated_at: string;
    created_by: string;
    is_active: boolean;
    client_id: string;
  };
}

const DocumentTypeViewer: React.FC<DocumentTypeViewerProps> = ({ documentType }) => {
  // Field table columns
  const fieldColumns = [
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          text: 'blue',
          number: 'green',
          date: 'purple',
          boolean: 'orange',
          email: 'cyan',
          phone: 'magenta',
          currency: 'gold'
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      }
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      width: 100,
      render: (required: boolean) => (
        <Tag color={required ? 'red' : 'default'} icon={required ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
          {required ? 'Required' : 'Optional'}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">No description</Text>
    },
    {
      title: 'Extraction Rules',
      key: 'extraction_rules',
      width: 250,
      render: (record: any) => {
        const rules = record.extraction_rules;
        if (!rules || (!rules.keywords && !rules.patterns)) {
          return <Text type="secondary">No rules</Text>;
        }

        const allItems = [
          ...(rules.patterns || []).map((item: string) => ({ text: item, type: 'pattern' })),
          ...(rules.keywords || []).map((item: string) => ({ text: item, type: 'keyword' }))
        ];

        const visibleItems = allItems.slice(0, 2);
        const hasMore = allItems.length > 2;

        return (
          <Space direction="vertical" size={4}>
            <div>
              {visibleItems.map((item, index) => (
                <Tag 
                  key={index} 
                  color={item.type === 'pattern' ? 'green' : 'blue'} 
                  style={{ fontSize: '11px', marginBottom: '2px' }}
                >
                  {item.text.length > 15 ? `${item.text.substring(0, 15)}...` : item.text}
                </Tag>
              ))}
              {hasMore && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  +{allItems.length - 2} more
                </Text>
              )}
            </div>
          </Space>
        );
      }
    }
  ];

  const getOCREngineIcon = (engine: string) => {
    switch (engine) {
      case 'gemini':
        return '🤖';
      case 'azure':
        return '☁️';
      case 'aws':
        return '📚';
      default:
        return '⚙️';
    }
  };

  const getOCREngineName = (engine: string) => {
    switch (engine) {
      case 'gemini':
        return 'Google Gemini';
      case 'azure':
        return 'Azure Document Intelligence';
      case 'aws':
        return 'AWS Textract';
      default:
        return engine;
    }
  };

  return (
    <div>
      {/* Status Alert */}
      <Alert
        message={`Document Type Status: ${documentType.is_active ? 'Active' : 'Inactive'}`}
        type={documentType.is_active ? 'success' : 'warning'}
        showIcon
        style={{ marginBottom: '16px' }}
      />

      {/* Basic Information */}
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            Basic Information
          </Space>
        }
        style={{ marginBottom: '16px' }}
      >
        <Descriptions column={2} labelStyle={{ fontWeight: 'bold' }}>
          <Descriptions.Item label="Document Type">
            <Text strong style={{ fontSize: '16px' }}>
              {documentType.document_type}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={documentType.is_active ? 'green' : 'red'}>
              {documentType.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            <Paragraph>{documentType.description}</Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="Schema Version">
            <Tag color="blue">{documentType.schema.version}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Field Count">
            <Tag color="purple">{documentType.schema.fields.length} fields</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {new Date(documentType.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {new Date(documentType.updated_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {documentType.created_by}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Schema Fields */}
      <Card 
        title={
          <Space>
            <InfoCircleOutlined />
            Schema Fields ({documentType.schema.fields.length})
          </Space>
        }
        style={{ marginBottom: '16px' }}
      >
        {documentType.schema.fields.length > 0 ? (
          <Table
            columns={fieldColumns}
            dataSource={documentType.schema.fields}
            rowKey="name"
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
            expandable={{
              expandedRowRender: (record) => {
                if (!record.description && !record.extraction_rules?.keywords && !record.extraction_rules?.patterns) {
                  return <Text type="secondary">No additional details</Text>;
                }
                
                return (
                  <div style={{ margin: '8px 0', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                    {record.description && (
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>Description: </Text>
                        <Text>{record.description}</Text>
                      </div>
                    )}
                    
                    {record.extraction_rules?.keywords && record.extraction_rules.keywords.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>Keywords: </Text>
                        <div style={{ marginTop: '4px' }}>
                          {record.extraction_rules.keywords.map((keyword: string, index: number) => (
                            <Tag key={index} color="blue" style={{ marginBottom: '4px', marginRight: '4px' }}>
                              {keyword}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {record.extraction_rules?.patterns && record.extraction_rules.patterns.length > 0 && (
                      <div>
                        <Text strong>Regex Patterns: </Text>
                        <div style={{ marginTop: '4px' }}>
                          {record.extraction_rules.patterns.map((pattern: string, index: number) => (
                            <Tag key={index} color="green" style={{ marginBottom: '4px', marginRight: '4px' }}>
                              <code style={{ fontSize: '11px' }}>{pattern}</code>
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              },
              rowExpandable: (record) => 
                !!(record.description || 
                   record.extraction_rules?.keywords?.length || 
                   record.extraction_rules?.patterns?.length)
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <InfoCircleOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
            <div>
              <Text type="secondary">No fields defined for this document type</Text>
            </div>
          </div>
        )}
      </Card>

      {/* Summary Statistics */}
      <Card title="Summary" style={{ marginTop: '16px' }}>
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#1890ff', fontWeight: 'bold' }}>
                {documentType.schema.fields.length}
              </div>
              <Text type="secondary">Total Fields</Text>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#f5222d', fontWeight: 'bold' }}>
                {documentType.schema.fields.filter(f => f.required).length}
              </div>
              <Text type="secondary">Required Fields</Text>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#52c41a', fontWeight: 'bold' }}>
                {documentType.schema.fields.filter(f => f.extraction_rules?.keywords?.length || f.extraction_rules?.patterns?.length).length}
              </div>
              <Text type="secondary">Fields with Rules</Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DocumentTypeViewer;
